import { LocalDate } from "@js-joda/core";
import { SalaryType } from "model/Salary";
import type { CreateWorkerEmploymentRequest } from "services/api/company/staff.service";
import type {
  SaveEmploymentResult,
  SaveEmploymentParams,
} from "../../profilePanel.contract";
import {
  parseIsoDateToLocalDateOrNull,
  toIsoStartOfDayUtcFromStockholmDate,
} from "web-apps/company/utils/timeAndDate";
import { getEmploymentFieldVisibility } from "../../../shared/staffEmploymentFieldVisibility.logic";

/**
 * Parses ISO date or date-time text into LocalDate.
 * Returns null when value is missing or invalid.
 */
export const toLocalDateOrNull = (
  value: string | null | undefined,
): LocalDate | null => {
  return parseIsoDateToLocalDateOrNull(value);
};

/**
 * Converts Stockholm start-of-day for LocalDate to a UTC ISO instant.
 */
export const toIsoDateStartOfDayUtc = (date: LocalDate): string => {
  return toIsoStartOfDayUtcFromStockholmDate(date);
};

const toIsoDateStartOfDayUtcOrNull = (value: string | null): string | null => {
  const parsedDate = toLocalDateOrNull(value);
  if (parsedDate == null) return null;
  return toIsoDateStartOfDayUtc(parsedDate);
};

/**
 * Parses a numeric string and clamps negative/invalid values to 0.
 */
export const toNonNegativeNumber = (value: string): number => {
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) return 0;
  return Math.max(0, parsedValue);
};

/**
 * Parses an integer string and clamps invalid/negative values to 0.
 */
export const toNonNegativeIntegerOrZero = (value: string): number => {
  const trimmedValue = value.trim();
  if (!/^\d+$/.test(trimmedValue)) return 0;
  const parsedValue = Number(trimmedValue);
  if (!Number.isSafeInteger(parsedValue)) return 0;
  return parsedValue;
};

const toEmploymentBasePayload = (
  params: SaveEmploymentParams,
): Omit<CreateWorkerEmploymentRequest, "salaryValue"> => {
  const form = params.editedEmploymentDetails;

  const workPercentagePercent = toNonNegativeNumber(form.workPercentage);
  const workPercentage =
    form.salaryType === SalaryType.Monthly
      ? Math.min(workPercentagePercent / 100, 1)
      : 0;

  // employmentType is always required (save is blocked until set), so the
  // "Undefined" fallback is unreachable; it is just the not-set sentinel.
  const employmentType = form.employmentType || "Undefined";

  return {
    startDate: toIsoDateStartOfDayUtc(params.effectiveFromDate),
    endDate: params.effectiveEndDate
      ? toIsoDateStartOfDayUtc(params.effectiveEndDate)
      : null,
    employmentType,
    employeeNumber: toNonNegativeIntegerOrZero(form.employeeNumber),
    employeeCategory: form.employeeCategory || "BlueCollar",
    salaryType: form.salaryType || "Undefined",
    workPercentage,
    paymentType: form.paymentType || "None",
    role: form.role,
    agreement: form.agreement,
    vacationDays: toNonNegativeIntegerOrZero(form.vacationDays),
    noticeDays: toNonNegativeIntegerOrZero(form.noticeDays),
    workplace: form.workplace,
    probationPeriodEndDate: toIsoDateStartOfDayUtcOrNull(
      params.probationPeriodEndDate,
    ),
  };
};

/**
 * Builds API payload for updating current worker employment data.
 */
export const buildUpdateStaffWorkerPayload = (
  params: SaveEmploymentParams,
): SaveEmploymentResult => {
  return {
    payload: toEmploymentBasePayload(params),
  };
};

/**
 * Builds API payload for creating a new worker employment.
 */
export const buildCreateWorkerEmploymentPayload = (
  params: SaveEmploymentParams,
): { payload: CreateWorkerEmploymentRequest } => {
  const form = params.editedEmploymentDetails;
  const base = toEmploymentBasePayload(params);
  const visibility = getEmploymentFieldVisibility(form.employmentType);

  // On create, fields hidden for the selected employment type are never sent
  // with stale input: forced fields use their forced value, the rest a default.
  // (Update keeps the existing values, so this is create-only.)
  return {
    payload: {
      ...base,
      // Salary model is required whenever visible (save is blocked until set),
      // so the visible "Undefined" fallback is unreachable; hidden forces Hourly.
      salaryType: visibility.showSalaryType
        ? form.salaryType || "Undefined"
        : "Hourly",
      employeeCategory: visibility.showEmployeeCategory
        ? base.employeeCategory
        : "BlueCollar",
      paymentType: visibility.showPaymentType ? base.paymentType : "None",
      workPercentage: visibility.showSalaryType ? base.workPercentage : 0,
      agreement: visibility.showContractTerms ? base.agreement : "",
      vacationDays: visibility.showContractTerms ? base.vacationDays : 0,
      noticeDays: visibility.showContractTerms ? base.noticeDays : 0,
      workplace: visibility.showContractTerms ? base.workplace : "",
      salaryValue: visibility.showSalary ? toNonNegativeNumber(form.salary) : 0,
    },
  };
};
